import { useContextKeycloak } from "@/app/module/keycloak/index";
import {
  clearProfileCacheByUserId,
  getCachedData,
  getProfileAccountsCacheKey,
  getProfileCacheKey,
  setCachedData,
} from "@/app/utils/profile-cache";
import type {
  AccountItemType,
  ProfileImageType,
  ProfileItemType,
} from "@/schemas/index";
import { useProfileService } from "@/services";
import { jwtDecode } from "jwt-decode";
import { useRef, useState } from "react";

const getUserIdFromToken = (token: string | undefined): string | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode<{ sub?: string; preferred_username?: string }>(
      token
    );
    return decoded.sub || decoded.preferred_username || null;
  } catch (error) {
    console.error("Failed to decode JWT token:", error);
    return null;
  }
};

export function useProfile() {
  const api = useProfileService();
  const { jwtToken } = useContextKeycloak();
  const userId = getUserIdFromToken(jwtToken);

  const profileCacheKey = userId
    ? getProfileCacheKey(userId)
    : null;
  const accountsCacheKey = userId
    ? getProfileAccountsCacheKey(userId)
    : null;

  const cachedProfile = profileCacheKey
    ? getCachedData<ProfileItemType>(profileCacheKey)
    : null;
  const cachedProfileAccounts = accountsCacheKey
    ? getCachedData<AccountItemType[]>(accountsCacheKey)
    : null;

  const [profile, setProfile] = useState<ProfileItemType | undefined>(
    cachedProfile ?? undefined
  );
  const [profileAccounts, setProfileAccounts] = useState<
    AccountItemType[] | undefined
  >(cachedProfileAccounts ?? undefined);
  const [profileImage, setProfileImage] = useState<ProfileImageType>();
  const [updated, setUpdated] = useState<boolean>();
  const [updatedCurrentAccount, setUpdatedCurrentAccount] = useState<boolean>();
  const [removed, setRemoved] = useState<boolean>();
  const currentPromiseFetchInit = useRef<any>();
  const currentPromiseFetchImageUpdate = useRef<any>();

  const fetchProfile = () => {
    if (currentPromiseFetchInit.current) {
      return currentPromiseFetchInit.current;
    }

    if (profileCacheKey && accountsCacheKey) {
      const cachedProfile = getCachedData<ProfileItemType>(profileCacheKey);
      const cachedProfileAccounts =
        getCachedData<AccountItemType[]>(accountsCacheKey);

      if (cachedProfile && cachedProfileAccounts) {
        setProfile(cachedProfile);
        setProfileAccounts(cachedProfileAccounts);
        return Promise.resolve([cachedProfile, cachedProfileAccounts]);
      }
    }

    currentPromiseFetchInit.current = Promise.all([
      api.fetchProfile(),
      api.fetchProfileAccounts(),
    ]);
    currentPromiseFetchInit.current
      .then(
        ([profile, profileAccounts]: [ProfileItemType, AccountItemType[]]) => {
          if (profileCacheKey && accountsCacheKey) {
            setCachedData(profileCacheKey, profile);
            setCachedData(accountsCacheKey, profileAccounts);
          }

          setProfile(profile);
          setProfileAccounts(profileAccounts);
        }
      )
      .finally(() => {
        currentPromiseFetchInit.current = null;
      });

    return currentPromiseFetchInit.current;
  };

  const fetchAvatar = async () => {
    const profileImage = await api.fetchProfileImage();
    setProfileImage(profileImage);
    return profileImage;
  };

  const fetchUserImage = async (login: ProfileItemType["Login"]) => {
    return await api.fetchUserImage(login);
  };

  const fetchUpdateProfileImage = async (data: ProfileImageType) => {
    if (currentPromiseFetchImageUpdate.current) {
      return currentPromiseFetchImageUpdate.current;
    }

    return (currentPromiseFetchImageUpdate.current = api
      .fetchRemoveProfileImage()
      .then(() => api.fetchUpdateProfileImage(data).catch())
      .then(() => api.fetchProfileImage().catch())
      .then((profileImage: any) => setProfileImage(profileImage))
      .finally(() => {
        currentPromiseFetchImageUpdate.current = null;
      }));
  };

  const fetchUpdateProfile = async (data: ProfileItemType) => {
    setUpdated(true);
    const result = await api.fetchUpdateProfile(data);
    setUpdated(false);

    // Update cache after profile update with user-specific key
    if (result && profileCacheKey) {
      setCachedData(profileCacheKey, data);
    }

    return result;
  };

  const fetchUpdateCurrentAccount = async (account: AccountItemType) => {
    setUpdatedCurrentAccount(true);
    const result = await api.fetchUpdateCurrentAccount(account);
    setUpdatedCurrentAccount(false);

    return result;
  };

  const fetchRemoveAccountProfile = async () => {
    setRemoved(true);
    const result = await api.removeAccountProfile();
    setRemoved(false);

    // Clear cache after account removal
    if (result) {
      clearProfileCacheByUserId(userId);
    }

    return result;
  };

  const clearCache = () => {
    clearProfileCacheByUserId(userId);
  };

  return {
    profile,
    updated,
    removed,
    profileAccounts,
    profileImage,
    updatedCurrentAccount,
    fetchUpdateCurrentAccount,
    fetchRemoveAccountProfile,
    fetchUpdateProfileImage,
    fetchUpdateProfile,
    fetchUserImage,
    fetchProfile,
    fetchAvatar,
    clearProfileCache: clearCache,
  };
}
